import { z } from 'zod';

/** Versão do envelope persistível; a extensão é opcional no cenário. */
export const CASHFLOW_RULES_SCHEMA_VERSION = 1 as const;
export const MAX_CASHFLOW_RULES = 500 as const;
export const CASHFLOW_MAX_AGE = 140 as const;

const MAX_SAFE_CENTS = BigInt(Number.MAX_SAFE_INTEGER);
const MONEY_PATTERN = /^(0|[1-9]\d*)\.\d{2}$/;

/**
 * Converte dinheiro textual canônico para centavos inteiros sem passar por
 * float. O valor textual continua sendo a autoridade persistível.
 */
export function moneyToCents(value: string): bigint {
  if (!MONEY_PATTERN.test(value)) {
    throw new RangeError('Dinheiro deve usar string não negativa com duas casas');
  }
  const [whole, fraction] = value.split('.');
  const cents = BigInt(whole!) * 100n + BigInt(fraction!);
  if (cents > MAX_SAFE_CENTS) {
    throw new RangeError('Dinheiro excede o limite seguro do mapper do motor');
  }
  return cents;
}

/** Dinheiro de uma regra planejada: não aceita sinais, escala implícita ou float. */
export const cashflowMoneySchema = z.string().refine((value) => {
  try {
    moneyToCents(value);
    return true;
  } catch {
    return false;
  }
}, 'Use dinheiro textual canônico, não negativo e com duas casas');

const ageSchema = z.number().int().min(0).max(CASHFLOW_MAX_AGE);
const stepYearsSchema = z.number().int().min(0).max(CASHFLOW_MAX_AGE);
const finiteNumberSchema = z.number().finite();
const noteSchema = z.string()
  .max(4_000)
  .refine((value) => isRenderableNote(value), 'Nota contém controle não renderizável');

/** Shape persistido correspondente à entrada real MonthlyCashflowRuleInput. */
export const persistedMonthlyCashflowRuleSchema = z.object({
  startAge: ageSchema,
  endAge: ageSchema,
  monthlyContribution: cashflowMoneySchema,
  monthlyWithdrawal: cashflowMoneySchema,
  contributionPercentStep: finiteNumberSchema,
  contributionFixedStep: finiteNumberSchema,
  contributionStepYears: stepYearsSchema,
  withdrawalPercentStep: finiteNumberSchema,
  withdrawalFixedStep: finiteNumberSchema,
  withdrawalStepYears: stepYearsSchema,
  note: noteSchema,
}).strict().superRefine((value, context) => {
  if (value.endAge < value.startAge) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['endAge'], message: 'Faixa mensal inválida' });
  }
  if (value.contributionPercentStep !== 0 && value.contributionFixedStep !== 0) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['contributionFixedStep'], message: 'Degraus percentual e fixo de aporte são conflitantes' });
  }
  if (value.withdrawalPercentStep !== 0 && value.withdrawalFixedStep !== 0) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['withdrawalFixedStep'], message: 'Degraus percentual e fixo de retirada são conflitantes' });
  }
  if (value.contributionStepYears === 0 && (value.contributionPercentStep !== 0 || value.contributionFixedStep !== 0)) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['contributionStepYears'], message: 'Degrau de aporte exige intervalo positivo' });
  }
  if (value.withdrawalStepYears === 0 && (value.withdrawalPercentStep !== 0 || value.withdrawalFixedStep !== 0)) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['withdrawalStepYears'], message: 'Degrau de retirada exige intervalo positivo' });
  }
});
export type PersistedMonthlyCashflowRule = z.infer<typeof persistedMonthlyCashflowRuleSchema>;

/** Shape persistido correspondente à entrada real AnnualCashflowRuleInput. */
export const persistedAnnualCashflowRuleSchema = z.object({
  age: ageSchema,
  endAge: ageSchema.nullable(),
  month: z.number().int().min(1).max(12).nullable(),
  annualContribution: cashflowMoneySchema,
  annualWithdrawal: cashflowMoneySchema,
  correctForInflation: z.boolean(),
  note: noteSchema,
}).strict().superRefine((value, context) => {
  if (value.endAge !== null && value.endAge < value.age) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['endAge'], message: 'Faixa anual inválida' });
  }
});
export type PersistedAnnualCashflowRule = z.infer<typeof persistedAnnualCashflowRuleSchema>;

/**
 * Contexto não duplicado no envelope: a regra pertence ao cenário e precisa
 * ser conferida contra o calendário/horizonte vigente antes de ser aplicada.
 */
export const cashflowRuleHorizonSchema = z.object({
  currentYear: z.number().int(),
  currentMonth: z.number().int().min(1).max(12),
  currentAge: ageSchema,
  lifeExpectancyAge: ageSchema,
}).strict().superRefine((value, context) => {
  if (value.lifeExpectancyAge < value.currentAge) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['lifeExpectancyAge'], message: 'Horizonte anterior à idade atual' });
  }
});
export type CashflowRuleHorizon = z.infer<typeof cashflowRuleHorizonSchema>;

/**
 * Envelope opcional e estritamente planejado. Não possui campos de ledger,
 * settlement, recibo, data realizada ou valor efetivamente realizado.
 */
export const plannedCashflowRulesSchema = z.object({
  schemaVersion: z.literal(CASHFLOW_RULES_SCHEMA_VERSION),
  state: z.literal('planned'),
  monthlyRules: z.array(persistedMonthlyCashflowRuleSchema).max(MAX_CASHFLOW_RULES),
  annualRules: z.array(persistedAnnualCashflowRuleSchema).max(MAX_CASHFLOW_RULES),
}).strict().superRefine((value, context) => {
  for (let left = 0; left < value.monthlyRules.length; left += 1) {
    const first = value.monthlyRules[left]!;
    for (let right = left + 1; right < value.monthlyRules.length; right += 1) {
      const second = value.monthlyRules[right]!;
      if (rangesOverlap(first.startAge, first.endAge, second.startAge, second.endAge)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['monthlyRules', right],
          message: 'Regras mensais sobrepostas são conflitantes e devem ser consolidadas antes da persistência',
        });
      }
    }
  }

  for (let left = 0; left < value.annualRules.length; left += 1) {
    const first = value.annualRules[left]!;
    const firstEnd = first.endAge ?? first.age;
    for (let right = left + 1; right < value.annualRules.length; right += 1) {
      const second = value.annualRules[right]!;
      const secondEnd = second.endAge ?? second.age;
      const monthConflict = first.month === null || second.month === null || first.month === second.month;
      if (monthConflict && rangesOverlap(first.age, firstEnd, second.age, secondEnd)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['annualRules', right],
          message: 'Regras anuais podem atingir o mesmo evento e são conflitantes',
        });
      }
    }
  }
});
export type PlannedCashflowRules = z.infer<typeof plannedCashflowRulesSchema>;

function rangesOverlap(firstStart: number, firstEnd: number, secondStart: number, secondEnd: number): boolean {
  return firstStart <= secondEnd && secondStart <= firstEnd;
}

function isRenderableNote(value: string): boolean {
  for (const character of value) {
    const codePoint = character.codePointAt(0)!;
    if (codePoint <= 8 || codePoint === 11 || codePoint === 12 || (codePoint >= 14 && codePoint <= 31) || codePoint === 127) {
      return false;
    }
  }
  return true;
}

function assertMonthlyRuleWithinHorizon(rule: PersistedMonthlyCashflowRule, horizon: CashflowRuleHorizon): void {
  if (rule.startAge < horizon.currentAge || rule.endAge > horizon.lifeExpectancyAge) {
    throw new RangeError('Regra monthly fora do horizonte planejado do cenário');
  }
}

function assertAnnualRuleWithinHorizon(rule: PersistedAnnualCashflowRule, horizon: CashflowRuleHorizon): void {
  const endAge = rule.endAge ?? rule.age;
  if (rule.age < horizon.currentAge || endAge > horizon.lifeExpectancyAge) {
    throw new RangeError('Regra annual fora do horizonte planejado do cenário');
  }
}

/** Faz parse estrito e valida calendário/horizonte sem preencher campos ausentes. */
export function validatePlannedCashflowRules(input: unknown, horizon: unknown): PlannedCashflowRules {
  const parsed = plannedCashflowRulesSchema.parse(input);
  const parsedHorizon = cashflowRuleHorizonSchema.parse(horizon);
  for (const rule of parsed.monthlyRules) assertMonthlyRuleWithinHorizon(rule, parsedHorizon);
  for (const rule of parsed.annualRules) assertAnnualRuleWithinHorizon(rule, parsedHorizon);
  return parsed;
}

/** Entrada numérica somente para a interface existente do motor; não é persistência. */
export interface MonthlyCashflowRuleEngineInput {
  startAge: number;
  endAge: number;
  monthlyContribution: number;
  monthlyWithdrawal: number;
  contributionPercentStep: number;
  contributionFixedStep: number;
  contributionStepYears: number;
  withdrawalPercentStep: number;
  withdrawalFixedStep: number;
  withdrawalStepYears: number;
  note: string;
}

/** Entrada numérica somente para a interface existente do motor; não é persistência. */
export interface AnnualCashflowRuleEngineInput {
  age: number;
  endAge: number | null;
  month: number | null;
  annualContribution: number;
  annualWithdrawal: number;
  correctForInflation: boolean;
  note: string;
}

export interface EngineCashflowRuleInputs {
  monthlyRules: readonly MonthlyCashflowRuleEngineInput[];
  annualRules: readonly AnnualCashflowRuleEngineInput[];
}

function moneyForEngine(value: string): number {
  moneyToCents(value);
  const numeric = Number(value);
  // O motor recebe number por sua interface histórica. Rejeite qualquer valor
  // cuja representação monetária não faça round-trip exato para o texto salvo.
  if (!Number.isFinite(numeric) || numeric.toFixed(2) !== value) {
    throw new RangeError('Dinheiro não tem representação exata para a interface numérica do motor');
  }
  return numeric;
}

/** Mapeia o contrato textual para inputs exatamente compatíveis com v1. */
export function mapPlannedCashflowRulesToEngineInputs(input: unknown, horizon: unknown): EngineCashflowRuleInputs {
  const rules = validatePlannedCashflowRules(input, horizon);
  return {
    monthlyRules: rules.monthlyRules.map((rule) => ({
      startAge: rule.startAge,
      endAge: rule.endAge,
      monthlyContribution: moneyForEngine(rule.monthlyContribution),
      monthlyWithdrawal: moneyForEngine(rule.monthlyWithdrawal),
      contributionPercentStep: rule.contributionPercentStep,
      contributionFixedStep: rule.contributionFixedStep,
      contributionStepYears: rule.contributionStepYears,
      withdrawalPercentStep: rule.withdrawalPercentStep,
      withdrawalFixedStep: rule.withdrawalFixedStep,
      withdrawalStepYears: rule.withdrawalStepYears,
      note: rule.note,
    })),
    annualRules: rules.annualRules.map((rule) => ({
      age: rule.age,
      endAge: rule.endAge,
      month: rule.month,
      annualContribution: moneyForEngine(rule.annualContribution),
      annualWithdrawal: moneyForEngine(rule.annualWithdrawal),
      correctForInflation: rule.correctForInflation,
      note: rule.note,
    })),
  };
}

export interface CashflowRuleEngineConstructors<MonthlyRule, AnnualRule> {
  monthly: new (input: MonthlyCashflowRuleEngineInput) => MonthlyRule;
  annual: new (input: AnnualCashflowRuleEngineInput) => AnnualRule;
}

export interface EngineCashflowRules<MonthlyRule, AnnualRule> {
  monthlyRules: readonly MonthlyRule[];
  annualRules: readonly AnnualRule[];
}

/**
 * Constrói as classes reais do motor por injeção explícita. Assim o pacote de
 * contratos não cria dependência circular nem inventa uma segunda implementação.
 */
export function mapPlannedCashflowRulesToEngine<MonthlyRule, AnnualRule>(
  input: unknown,
  horizon: unknown,
  constructors: CashflowRuleEngineConstructors<MonthlyRule, AnnualRule>,
): EngineCashflowRules<MonthlyRule, AnnualRule> {
  const mapped = mapPlannedCashflowRulesToEngineInputs(input, horizon);
  return {
    monthlyRules: mapped.monthlyRules.map((rule) => new constructors.monthly(rule)),
    annualRules: mapped.annualRules.map((rule) => new constructors.annual(rule)),
  };
}
